/**
 * useMyFranchises — SOLE network owner for subscriptions/my-franchises.
 *
 * Module-scope singleton: every caller shares the same reactive `rows` ref.
 * `fetchMyFranchises()` is the single network call — GET subscriptions/my-franchises.
 *
 * No filter scoping (useFiltro is intentionally NOT used: the endpoint is
 * user-scoped, not filter-scoped — filtering would hide the lapsed franchises
 * the user came to recover).
 *
 * All network lives here (no_httpservice_in_vue rule).
 * Call `_reset()` in afterEach for test isolation.
 */
import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_MyFranchiseRow } from '../contracts/Subscriptions_MyFranchiseRow'

// Side-effect import: registers the contract with the responseContract registry.
import '../contracts/Subscriptions_MyFranchisesResponse'

// ── Module-scope singleton state ────────────────────────────────────────────

const rows = ref<Subscriptions_MyFranchiseRow[]>([])

// ── Public composable ────────────────────────────────────────────────────────

export function useMyFranchises() {
  /**
   * fetchMyFranchises — GET subscriptions/my-franchises.
   *
   * Returns the raw response envelope. The Page layer unwraps resp.dados.rows ?? [].
   * On error: logs and re-throws (no silent catch — the caller sees the error).
   */
  async function fetchMyFranchises(): Promise<unknown> {
    try {
      const resp = await httpService.get(
        `${config.api.sistemaHost}/subscriptions/my-franchises`,
        { responseContract: 'Subscriptions_MyFranchisesResponse' },
      )
      const envelope = resp as { dados: { rows: Subscriptions_MyFranchiseRow[] } }
      rows.value = envelope.dados?.rows ?? []
      log('useMyFranchises', 'fetchMyFranchises', 'Franchises carregadas', { count: rows.value.length })
      return resp
    } catch (err) {
      log('useMyFranchises', 'fetchMyFranchises', 'Erro ao carregar franchises', {
        message: (err as Error)?.message,
      })
      throw err
    }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    rows.value = []
  }

  return {
    rows,
    fetchMyFranchises,
    _reset,
  }
}
